import { EquipmentTyre, EquipmentWheel } from 'types/Equipment';
import axios from '../utils/axios';

export async function getEquipmentCatalog(): Promise<any> {
  return axios.get('/equipment-catalog').then((res) => res.data);
}

export async function getManufacturers(): Promise<any> {
  return axios.get('/manufacturers').then((res) => res.data);
}

export async function getWheelCircumferenceData(): Promise<any> {
  return axios.get('/equipment-team-rolling-radii?scope=plotting_data').then((res) => {
    // Server returns an array with the plotting data as the first element
    const responseData = res.data;

    if (Array.isArray(responseData) && responseData.length > 0) {
      return responseData[0];
    }
    return {};
  });
}

export interface RollingRadiiElement {
  id: string;
  id_type: string;
  id2: string | null;
  id2_type: string | null;
  tyre_id: string;
  inner_wheel_width: number;
  tyre_pressure: number;
  wheel_circumference_calc: number | null;
}

export interface RollingRadiiAverage {
  inner_wheel_width: number;
  tyre_pressure: number;
  average: number;
  n: number;
}

export interface RollingRadiiTyre {
  tyre_id: string;
  tyre_name: string | null;
  manufacturer: string | null;
  elements: RollingRadiiElement[];
  averages: RollingRadiiAverage[];
}

export async function getRollingRadiiElements(
  sessionIds?: string[],
): Promise<RollingRadiiTyre[]> {
  let url = '/equipment-team-rolling-radii?scope=elements';
  if (sessionIds && sessionIds.length > 0) {
    url += `&session_ids=${encodeURIComponent(sessionIds.join(','))}`;
  }
  return axios.get(url).then((res) => (Array.isArray(res.data) ? res.data : []));
}

export async function addManufacturer(data: { name: string; notes?: string }): Promise<any> {
  return axios.post('/manufacturers', data).then((res) => res.data);
}

export async function addFrame(data: {
  name: string;
  manufacturer_id: string;
  designation?: string;
  part_number?: string;
  size_nominal?: string;
  notes?: string;
}): Promise<any> {
  return axios.post('/equipment-frames', data).then((res) => res.data);
}

export async function addTyre(data: {
  name: string;
  manufacturer_id: string;
  designation?: string;
  part_number?: string;
  diameter_mm: number;
  width_mm?: number;
  mass_g?: number;
  notes?: string;
}): Promise<any> {
  return axios.post('/equipment-tyres', data).then((res) => res.data);
}

export async function addWheelFront(data: {
  name: string;
  manufacturer_id: string;
  designation?: string;
  part_number?: string;
  diameter_mm: number;
  depth_mm?: number;
  width_inner_mm?: number;
  width_outer_mm?: number;
  mass_g?: number;
  notes?: string;
}): Promise<any> {
  return axios.post('/equipment-wheels-front', data).then((res) => res.data);
}

export async function addWheelRear(data: {
  name: string;
  manufacturer_id: string;
  designation?: string;
  part_number?: string;
  diameter_mm: number;
  depth_mm?: number;
  width_inner_mm?: number;
  width_outer_mm?: number;
  mass_g?: number;
  notes?: string;
}): Promise<any> {
  return axios.post('/equipment-wheels-rear', data).then((res) => res.data);
}

export async function addEquipmentTeamParameter(data: {
  manufacturer_id?: string;
  frame_id?: string;
  tyre_id?: string;
  wheel_front_id?: string;
  wheel_rear_id?: string;
  keep_private?: boolean;
  code: number;
  data?: Record<string, any>;
}): Promise<any> {
  return axios.post('/equipment-team-parameters', data).then((res) => res.data);
}

export async function updateEquipmentTeamParameter(
  id: string,
  data: {
    keep_private?: boolean;
    data?: Record<string, any>;
  },
): Promise<any> {
  return axios.put(`/equipment-team-parameters/${id}`, data).then((res) => res.data);
}

export async function deleteEquipmentTeamParameter(id: string): Promise<any> {
  return axios.delete(`/equipment-team-parameters/${id}`).then((res) => res.data);
}

// Update functions
export async function updateManufacturer(id: string, data: { name: string; notes?: string }): Promise<any> {
  return axios.put(`/manufacturers/${id}`, data).then((res) => res.data);
}

export async function updateFrame(
  id: string,
  data: {
    name: string;
    manufacturer_id: string;
    designation?: string;
    part_number?: string;
    size_nominal?: string;
    notes?: string;
  },
): Promise<any> {
  return axios.put(`/equipment-frames/${id}`, data).then((res) => res.data);
}

export async function updateTyre(id: string, data: EquipmentTyre): Promise<any> {
  return axios.put(`/equipment-tyres/${id}`, data).then((res) => res.data);
}

export async function updateWheelFront(id: string, data: EquipmentWheel): Promise<any> {
  return axios.put(`/equipment-wheels-front/${id}`, data).then((res) => res.data);
}

export async function updateWheelRear(id: string, data: EquipmentWheel): Promise<any> {
  return axios.put(`/equipment-wheels-rear/${id}`, data).then((res) => res.data);
}

// Delete functions
export async function deleteManufacturer(id: string): Promise<any> {
  return axios.delete(`/manufacturers/${id}`).then((res) => res.data);
}

export async function deleteFrame(id: string): Promise<any> {
  return axios.delete(`/equipment-frames/${id}`).then((res) => res.data);
}

export async function deleteTyre(id: string): Promise<any> {
  return axios.delete(`/equipment-tyres/${id}`).then((res) => res.data);
}

export async function deleteWheelFront(id: string): Promise<any> {
  return axios.delete(`/equipment-wheels-front/${id}`).then((res) => res.data);
}

export async function deleteWheelRear(id: string): Promise<any> {
  return axios.delete(`/equipment-wheels-rear/${id}`).then((res) => res.data);
}
